import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

import _ from 'lodash';
import { BehaviorSubject } from 'rxjs';

interface IContact {
  index: number
  type: string // or should we use literal type?
  companyTrustName: string
  contactName: string
  contactPosition: string
  contactMobile: string
  contactEmail: string
  isDisabled?: boolean
}
interface IContactUpdateInput extends Pick<IContact,
  'companyTrustName' |
  'contactName' |
  'contactPosition' |
  'contactMobile' |
  'contactEmail'
  > {
}

@Injectable()
export class ContactService {

  private contactList = [];
  public selectedContacts:string[] = [];

  private contactListSubject = new BehaviorSubject<any>(null);
  public contactListListener = this.contactListSubject.asObservable();

  private selectedContactsSubject = new BehaviorSubject<any>(null);
  public selectedContactsListener = this.selectedContactsSubject.asObservable();

  // can be change?
  public contactSelectedFieldName = 'contactSelected';

  private sendSelectedContactData () {
    this.selectedContactsSubject.next(this.selectedContacts);
  }
  /**
   * add contact to the contactList
   * @param contact 
   */
  public save (contact: IContact) {
    const {
      index,
      type,
      companyTrustName,
      contactEmail,
      contactMobile,
      contactName,
      contactPosition,
      isDisabled = false
    } = contact;

    const identity = this.getContactIdentity(type, index);
    const contactIndex = this.contactList.findIndex((c) => c.identity === identity);
    // check if the contact is already exists in the `contactList` by comparing `identify` field.
    if (contactIndex === -1) {
      this.contactList.push({
        index,
        type,
        identity,
        companyTrustName,
        contactEmail,
        contactMobile,
        contactName,
        contactPosition,
        isDisabled,
      });
      this.emit();
    } else {
      this.updateContactList(identity, {
        companyTrustName,
        contactEmail,
        contactMobile,
        contactName,
        contactPosition,
      });
      // ## TODO ##
      // what to do if the contact is already exists?
    }
  }

  private addContactField (fg: FormGroup, field: string) {
    const isExists = !!(fg.get(field))
    if (!isExists) {
      fg.addControl(field, new FormControl(''), {emitEvent: false});
    }
    return fg.get(field);
  }

  /**
   * add the contact fields in the form array if not existed.
   * @param formArray 
   * @param type 
   */
  public addContactFieldsIfNotExists (formArray: FormArray, type: string) {
    const fieldArray = this.getFieldNames(type);
    formArray.controls.forEach((fg: FormGroup, index: number) => {
      fieldArray.forEach((field: any) => this.addContactField(fg, field));
      this.disableContact(fg, this.getContactIdentity(type, index));
    });
  }

  public checkContactDetails(formArray: FormArray, type: string) {
    formArray.controls.forEach((fg: FormGroup) => {
      if (fg.value[this.contactSelectedFieldName]) {
        const selectedContactIdentity = fg.value[this.contactSelectedFieldName];
        if (this.contactList.length >= 1) {
          const data: any = this.getContact(selectedContactIdentity);
          if (data) {
            this.addToSelectedContacts(selectedContactIdentity);
            fg.get(`${type}ContactName`).patchValue(data[`contactName`], {emitEvent: false});
            fg.get(`${type}ContactPosition`).patchValue(data[`contactPosition`], {emitEvent: false});
            fg.get(`${type}ContactMobile`).patchValue(data[`contactMobile`], {emitEvent: false});
            fg.get(`${type}ContactEmail`).patchValue(data[`contactEmail`], {emitEvent: false});
            this.updateContactControlEnableStatus(fg, false, type);
            
          }
          if (!data || (!data[`contactEmail`] && !data[`contactMobile`] && !data[`contactPosition`] && !data[`contactName`])) {
            fg.get(this.contactSelectedFieldName).patchValue(null, {emitEvent: false});
            fg.enable({emitEvent: false});
          }
        }
      }
    })
  }

  /**
   * update the contact
   * @param data 
   * @param index 
   * @param type 
   */
  updateContactList(identity: string, data: IContactUpdateInput) {
    const contact = this.getContact(identity);
    let isUpdated = false;
    const updateFieldValue = (contact: any, input: IContactUpdateInput, fieldName: string) => {
      if (contact[fieldName] !== input[fieldName]) {
        contact[fieldName] = input[fieldName];
        isUpdated = true;
      }
    }
    if (contact) {
      !data.companyTrustName && (data.companyTrustName = contact.type + ' ' + (contact.index + 1));
      updateFieldValue(contact, data, 'companyTrustName');
      updateFieldValue(contact, data, 'contactName');
      updateFieldValue(contact, data, 'contactPosition');
      updateFieldValue(contact, data, 'contactMobile');
      updateFieldValue(contact, data, 'contactEmail');
      if (isUpdated) {
        this.emit();
      }
    }
  }

  public disableContact (fg: FormGroup, identity: string) {
    const foundContactForDisabling = this.getContact(identity);
    if (foundContactForDisabling) {
      foundContactForDisabling.isDisabled = !!(fg.get(this.contactSelectedFieldName)?.value);
    }
  }

  public getContact (identity: string) {
    return this.contactList.find((contact) => contact.identity === identity);
  }
  public getContactIdentity (type: string, index: number) {
    return `${type.toLowerCase()}-${index}`.trim();
  }

  public clearContactList () {
    this.contactList = [];
    this.emit();
  }

  private emit () {
    this.contactListSubject.next(this.contactList);
  }

  public setContactValidators(fg: FormGroup, set: Boolean, data: any, type: string) {
    const updateFC = (fcName: string, value: any) => {
      const contactNameFC = fg.get(fcName);
      if (contactNameFC) {
        if (!set) {
          contactNameFC.addValidators(Validators.required);
        } else {
          contactNameFC.clearValidators();
        }
        contactNameFC.setValue(value, {emitEvent: false});
        contactNameFC.updateValueAndValidity({emitEvent: false});
      }
    }
    type = type.toLowerCase();
    if (!set) {
      updateFC(`${type}ContactName`, null);
      updateFC(`${type}ContactPosition`, null);
      updateFC(`${type}ContactMobile`, null);
      updateFC(`${type}ContactEmail`, null);
    } else {
      updateFC(`${type}ContactName`, (data.contactName) ? data.contactName : data.companyContactName);
      updateFC(`${type}ContactPosition`, (data.contactPosition) ? data.contactPosition : data.companyContactPosition);
      updateFC(`${type}ContactMobile`, (data.contactMobile) ? data.contactMobile : data.companyContactMobile);
      updateFC(`${type}ContactEmail`, (data.contactEmail) ? data.contactEmail : data.companyContactEmail);
      fg.get(this.contactSelectedFieldName)?.patchValue((data.identity) ? data.identity : data[this.contactSelectedFieldName], {emitEvent: false});
      fg.get(this.contactSelectedFieldName)?.updateValueAndValidity({emitEvent: false});
    }
  }

  /**
   * update the contact form control enable status
   * @param fg 
   * @param status 
   * @param type 
   */
  public updateContactControlEnableStatus (fg: FormGroup, status: boolean, type: string) {
    type = type.toLowerCase();
    if (status) {
      fg.get(`${type}ContactName`)?.enable({emitEvent: false});
      fg.get(`${type}ContactMobile`)?.enable({emitEvent: false});
      fg.get(`${type}ContactPosition`)?.enable({emitEvent: false});
      fg.get(`${type}ContactEmail`)?.enable({emitEvent: false});
    } else {
      fg.get(`${type}ContactName`)?.disable({emitEvent: false});
      fg.get(`${type}ContactMobile`)?.disable({emitEvent: false});
      fg.get(`${type}ContactPosition`)?.disable({emitEvent: false});
      fg.get(`${type}ContactEmail`)?.disable({emitEvent: false});
    }
  }

  private getSelectedContactIndex (identity) {
    return this.selectedContacts.findIndex(id => id === identity);
  }
  public addToSelectedContacts (identity: string) {
    if (this.getSelectedContactIndex(identity) === -1) {
      this.selectedContacts.push(identity);
      this.sendSelectedContactData();
    }
    return this.selectedContacts;
  }
  public removeToSelectedContacts (identity: string) {
    const index = this.getSelectedContactIndex(identity);
    if (index >= 0) {
      this.selectedContacts.splice(index, 1);
      this.sendSelectedContactData();
    }
    return this.selectedContacts;
  }

  /**
   * 
   * @param type 
   * @returns 
   */
  public getFieldNames (type: string) {
    return ['Name', 'ContactName', 'ContactPosition', 'ContactMobile', 'ContactEmail', this.contactSelectedFieldName].map((f) => f !== this.contactSelectedFieldName ? `${type.toLowerCase()}${f}`: f);
  }
  
  public getContactFields (payload: any, type: string) {
    return payload ? this.getFieldNames(type).reduce((obj, f) => {
      if (payload[f]) {
        obj[f] = payload[f];
      }
      return obj;
    }, {}) : {};
  }
}
